<template>
    <article>
        <v-tabs v-model="tab" height="40" slider-size="2" show-arrows class="mb-4">
            <v-tab :to="{hash: 'ce'}" tab-value="ce">Community Edition</v-tab>
            <v-tab :to="{hash: 'ee'}" tab-value="ee">Enterprise Edition</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <v-tab-item value="ce" eager transition="none">
            <p>
                Add the Coroot helm chart repo:
            </p>
            <CodeSnippet>
                <pre v-hl.bash>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update coroot
                </pre>
            </CodeSnippet>

            <p>
                Next, install the chart that includes:
                <v-checkbox label="Coroot" v-model="coroot" disabled hide-details color="green" dense />
                <v-checkbox label="Prometheus (+ kube-state-metrics)" disabled v-model="prometheus" hide-details color="green" dense />
                <v-checkbox label="coroot-node-agent" hide-details disabled v-model="agent" color="green" dense />
                <v-checkbox label="Continuous profiling (ClickHouse)" v-model="profiling" hide-details color="green" dense />
                <v-checkbox label="Distributed tracing (ClickHouse)" v-model="tracing" hide-details color="green" dense />
                <v-checkbox label="Logs (ClickHouse)" v-model="logs" hide-details color="green" dense />
            </p>
            <div class="mb-2">Exposing Coroot for external access:</div>
            <v-row>
                <v-col cols="12" sm="5">
                    <v-select v-model="accessMode" :items="accessModes" dense outlined hide-details menu-props="offsetY" />
                </v-col>
                <v-col>
                    <v-text-field v-if="accessMode === 'ingress'" v-model="ingressHostname" dense outlined hide-details>
                        <template #prepend-inner>
                            <span class="mt-1 pr-1 grey--text text-no-wrap">Ingress host:</span>
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
            <CodeSnippet>
                <div class="hl">{{install}}</div>
            </CodeSnippet>

            <p v-if="accessMode === 'ingress'">
                Visit Coroot at the Ingress URL: <a :href="`http://${ingressHostname}`" target="_blank">http://{{ingressHostname}}</a>
            </p>

            <p v-else-if="accessMode === 'nodeport'">
                Get the Coroot URL by running these commands in the same shell:
                <CodeSnippet>
                    <pre v-hl.bash>
export NODE_PORT=$(kubectl get --namespace coroot -o jsonpath="{.spec.ports[0].nodePort}" services coroot)
export NODE_IP=$(kubectl get nodes --namespace coroot -o jsonpath="{.items[0].status.addresses[0].address}")
echo http://$NODE_IP:$NODE_PORT
                    </pre>
                </CodeSnippet>
            </p>

            <div v-else>
                <p>Forward the Coroot port to your machine:</p>
                <CodeSnippet>
                    <pre v-hl.bash>
kubectl port-forward -n coroot service/coroot 8080:8080
                    </pre>
                </CodeSnippet>
                <p>
                    Then, you can access Coroot at <a href="http://localhost:8080" target="_blank">http://localhost:8080</a>
                </p>
            </div>

            <h2>Upgrade Coroot</h2>

            <p>To upgrade Coroot to the latest version:</p>

            <CodeSnippet>
                <pre v-hl.bash>
helm repo update coroot
helm upgrade coroot --namespace coroot coroot/coroot
                </pre>
            </CodeSnippet>

            <h2>Uninstall Coroot</h2>

            <p>To uninstall Coroot run the following command:</p>

            <CodeSnippet>
                <pre v-hl.bash>
helm uninstall coroot -n coroot
                </pre>
            </CodeSnippet>
            </v-tab-item>

            <v-tab-item value="ee" eager transition="none">
                <v-alert color="info" outlined text class="mt-5">
                    The Enterprise Edition is a paid subscription (from $1 per CPU core/month) that offers extra features and priority support.
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <p>
                    Add the Coroot helm chart repo:
                </p>
                <CodeSnippet>
                <pre v-hl.bash>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update coroot
                </pre>
                </CodeSnippet>

                <p>
                    Next, install the Coroot Operator:
                </p>

                <CodeSnippet>
                    <div class="hl">helm install -n coroot --create-namespace coroot-operator coroot/coroot-operator</div>
                </CodeSnippet>

                <p>Install the Coroot Enterprise Edition:</p>

                <v-alert color="info" outlined text class="mt-5">
                    To install Coroot Enterprise Edition, you'll need a license (from $1 per CPU core/month).
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <div class="subtitle-1">License key:</div>
                <v-form>
                    <v-text-field
                        v-model="license_key"
                        placeholder="COROOT-11111111-1111-1111-1111-111111111111"
                        outlined
                        dense
                    />
                </v-form>

                <CodeSnippet>
                    <pre class="hl">helm install -n coroot coroot coroot/coroot-ee --set "licenseKey={{license_key || 'COROOT-LICENSE-KEY-HERE'}},clickhouse.shards=2,clickhouse.replicas=2"</pre>
                </CodeSnippet>


                <p>Forward the Coroot port to your machine:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
kubectl port-forward -n coroot service/coroot-coroot 8080:8080
                    </pre>
                </CodeSnippet>
                <p>
                    Then, you can access Coroot at <a href="http://localhost:8080" target="_blank">http://localhost:8080</a>
                </p>

                <h2>Upgrade Coroot</h2>

                <p>The Coroot Operator for Kubernetes automatically upgrades all components.</p>

                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                <pre v-hl.bash>
helm uninstall coroot -n coroot
helm uninstall coroot-operator -n coroot
                </pre>
                </CodeSnippet>
            </v-tab-item>
        </v-tabs-items>
    </article>
</template>

<script>

export default {
    data() {
        return {
            tab: null,
            coroot: true,
            prometheus: true,
            agent: true,
            profiling: true,
            tracing: true,
            logs: true,
            accessMode: '',
            ingressHostname: 'coroot.local',
            license_key: '',
        }
    },
    computed: {
        install() {
            let cmd = 'helm install --namespace coroot --create-namespace';
            if (!this.prometheus) {
                cmd += ' --set prometheus.enabled=false';
            }
            if (!this.agent) {
                cmd += ' --set node-agent.enabled=false';
            }
            if (!this.profiling && !this.tracing && !this.logs) {
                cmd += ' --set clickhouse.enabled=false';
            }
            if (!this.tracing) {
                cmd += ' --set node-agent.tracesEndpoint=""';
            }
            if (!this.logs) {
                cmd += ' --set node-agent.logsEndpoint=""';
            }
            if (!this.profiling) {
                cmd += ' --set node-agent.profilesEndpoint=""';
            }
            if (this.accessMode === 'ingress') {
                cmd += ' --set corootCE.ingress.enabled=true --set corootCE.ingress.hostname='+this.ingressHostname;
            }
            if (this.accessMode === 'nodeport') {
                cmd += ' --set corootCE.service.type=NodePort';
            }
            cmd += ' coroot coroot/coroot';
            return cmd;
        },
        accessModes() {
            return [
                {value: '', text: "Don't expose (port forward)"},
                {value: 'nodeport', text: 'NodePort'},
                {value: 'ingress', text: 'Ingress'},
            ];
        },
    },
}
</script>

<style scoped>
</style>
